import { createContext, useContext, useState, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import * as api from '../services/api'
import { setOnUnauthorized } from '../services/api'

const AuthContext = createContext(null)

const AUTH_KEY = 'emailtest_authenticated'
const USER_KEY = 'emailtest_user'

export function AuthProvider({ children }) {
  const navigate = useNavigate()
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem(AUTH_KEY) === 'true'
  })
  const [user, setUser] = useState(() => {
    const stored = localStorage.getItem(USER_KEY)
    return stored ? JSON.parse(stored) : null
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  const login = useCallback(async (username, password) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await api.login(username, password)

      // Store auth state (cookie is set automatically by the API)
      localStorage.setItem(AUTH_KEY, 'true')
      localStorage.setItem(USER_KEY, JSON.stringify({ email: response.email }))

      setIsAuthenticated(true)
      setUser({ email: response.email })
      navigate('/')

      return true
    } catch (err) {
      setError(err.message)
      return false
    } finally {
      setIsLoading(false)
    }
  }, [navigate])

  const logout = useCallback(async () => {
    setIsLoading(true)

    try {
      await api.logout()
    } catch {
      // Ignore logout errors - we'll clear local state anyway
    } finally {
      // Clear local auth state
      localStorage.removeItem(AUTH_KEY)
      localStorage.removeItem(USER_KEY)
      setIsAuthenticated(false)
      setUser(null)
      setIsLoading(false)
      navigate('/login')
    }
  }, [navigate])

  useEffect(() => {
    setOnUnauthorized(() => {
      localStorage.removeItem(AUTH_KEY)
      localStorage.removeItem(USER_KEY)
      setIsAuthenticated(false)
      setUser(null)
      navigate('/login')
    })
    return () => setOnUnauthorized(null)
  }, [navigate])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  const value = {
    isAuthenticated,
    user,
    isLoading,
    error,
    login,
    logout,
    clearError
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default useAuth
